"use client";

import type { SearchIntent } from "@/lib/api";
import { nomePaese } from "@/lib/api";

/**
 * Mostra all'utente che cosa ha capito il sistema dalla sua frase, in forma
 * modificabile. È la differenza fra una ricerca che si può correggere e una
 * scatola nera: se "piccoli lotti" è diventato "massimo 100 pezzi" e non era
 * quello che intendevi, lo vedi e lo togli.
 */

type Chiave = keyof SearchIntent;

type Chip = {
  chiave: Chiave;
  valore?: string;
  etichetta: string;
  categoria: string;
};

const CATEGORIE: Partial<Record<Chiave, string>> = {
  countries: "Paese",
  materials: "Materiale",
  processes: "Lavorazione",
  sectors: "Settore",
  certifications: "Certificazione",
  nace_prefixes: "NACE",
  max_order_quantity: "Lotto minimo",
  max_lead_time_days: "Consegna",
};

export function chipsDaIntent(intent: SearchIntent): Chip[] {
  const chips: Chip[] = [];

  const liste: [Chiave, (v: string) => string][] = [
    ["countries", nomePaese],
    ["materials", (v) => v],
    ["processes", (v) => v],
    ["sectors", (v) => v],
    ["certifications", (v) => v.toUpperCase()],
    ["nace_prefixes", (v) => v],
  ];

  for (const [chiave, formatta] of liste) {
    for (const valore of (intent[chiave] as string[]) ?? []) {
      chips.push({
        chiave,
        valore,
        etichetta: formatta(valore),
        categoria: CATEGORIE[chiave]!,
      });
    }
  }

  if (intent.max_order_quantity !== null) {
    chips.push({
      chiave: "max_order_quantity",
      etichetta: `fino a ${intent.max_order_quantity} pezzi`,
      categoria: CATEGORIE.max_order_quantity!,
    });
  }
  if (intent.max_lead_time_days !== null) {
    chips.push({
      chiave: "max_lead_time_days",
      etichetta: `entro ${intent.max_lead_time_days} giorni`,
      categoria: CATEGORIE.max_lead_time_days!,
    });
  }

  return chips;
}

export function rimuoviDaIntent(intent: SearchIntent, chip: Chip): SearchIntent {
  if (chip.valore === undefined) {
    return { ...intent, [chip.chiave]: null };
  }
  const attuali = intent[chip.chiave] as string[];
  return { ...intent, [chip.chiave]: attuali.filter((v) => v !== chip.valore) };
}

export function FilterChips({
  intent,
  onRimuovi,
}: {
  intent: SearchIntent;
  onRimuovi: (chip: Chip) => void;
}) {
  const chips = chipsDaIntent(intent);
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted">Ho capito:</span>
      {chips.map((chip) => (
        <button
          key={`${chip.chiave}:${chip.valore ?? ""}`}
          type="button"
          onClick={() => onRimuovi(chip)}
          title={`Togli il filtro ${chip.categoria.toLowerCase()}`}
          className="group inline-flex items-center gap-1.5 rounded-full border border-line bg-surface py-1 pl-3 pr-2 text-sm transition hover:border-accent"
        >
          <span className="text-muted">{chip.categoria}</span>
          <span className="font-medium">{chip.etichetta}</span>
          <span
            aria-hidden
            className="text-muted transition group-hover:text-accent"
          >
            ×
          </span>
        </button>
      ))}
    </div>
  );
}

export type { Chip };
